import {
    Badge,
    classNames,
    Dialog,
    DialogAlignment,
    KeyPad,
    layout,
    MessageOverlay,
    Overlay,
    PushButton,
    TextInput,
    theme,
    useAsync,
    useAsyncCallback,
    usePlatform,
    useTypeRef
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {defined} from "@variocube/app-common";
import {DateTime} from "luxon";
import {dialogStyles} from "./dialogStyles";
import {DefaultCodeLength} from "../model";
import {stylesheet} from "typestyle";
import {useLocalization} from "../../i18n";

interface AccessLockerDialogProps {
    open: boolean;
    onClose: () => void;
}

class OpenError extends Error {}
class ReleaseError extends Error {}

export function AccessLockerDialog({open, onClose}: AccessLockerDialogProps) {
    const {t} = useLocalization();
    const platform = usePlatform();
    const [type, setType] = useTypeRef();
    const [code, setCode] = useState("");

    const moreDigits = useMemo(() => code.length < DefaultCodeLength, [code]);

    const occupancies = useAsync(async () => {
        if (!moreDigits) {
            return await platform.getOccupancies(code);
        }
    }, [platform, moreDigits, code]);

    const hasOccupancies = defined(occupancies.result) && occupancies.result.length > 0;

    const openLocker = useAsyncCallback(async (release?: boolean) => {
        if (occupancies.result) {
            for (const occupancy of occupancies.result) {
                try {
                    await platform.openBoxLock(occupancy.boxNumber);
                }
                catch (error) {
                    throw new OpenError();
                }
                if (release) {
                    try {
                        await platform.updateOccupancy(occupancy.uuid, {
                            releasedAt: DateTime.local().toISO()
                        }, true);
                    }
                    catch (error) {
                        throw new ReleaseError();
                    }
                }
            }
            return true;
        }
    }, [occupancies, platform]);

    const openAndRelease = useCallback(() => openLocker.execute(true), [openLocker]);

    useEffect(() => {
        if (open) {
            setCode("");
            occupancies.clear();
            openLocker.clear();
        }
    }, [open, occupancies.clear, openLocker.clear]);

    const openErrorMessage = useMemo(() => {
        if (openLocker.error instanceof ReleaseError) {
            return t("EndOccupancyFailed");
        }
        else if (openLocker.error instanceof OpenError) {
            return t("OpenLockerFailed");
        }
        else if (openLocker.error) {
            return t("InternalError");
        }
    }, [openLocker.error]);

    return (
        <Dialog open={open} onBackdropClick={onClose} align={DialogAlignment.Bottom}>
            <div className={layout.horizontal}>
                <div className={classNames(layout.vertical, layout.spaceBetween, dialogStyles.info)}>
                    <div>
                        <div className={dialogStyles.heading}>
                            {t("EnterAccessCode")}
                        </div>
                        <div className={dialogStyles.description}>
                            {t("EnterAccessCodeDescription")}
                        </div>
                    </div>
                    <div>
                        {occupancies.loading && (
                            <Badge>{t("CheckingCode")}</Badge>
                        )}
                        {!occupancies.loading && occupancies.result?.length == 0 && (
                            <Badge error>{t("InvalidCode")}</Badge>
                        )}
                        {!code && (
                            <Badge>{t("EnterAccessCode")}</Badge>
                        )}
                        {code && moreDigits && (
                            <Badge>{t("MoreDigitsNeeded")}</Badge>
                        )}
                    </div>
                    <div>
                        <PushButton onClick={onClose} disabled={occupancies.loading} class={dialogStyles.button}>
                            {t("Cancel")}
                        </PushButton>
                    </div>
                </div>
                <div className={dialogStyles.action}>
                    <TextInput
                        type={"password"}
                        onChange={setCode}
                        disabled={occupancies.loading}
                        typeRef={setType}
                        value={code}
                    />
                    <KeyPad onType={type} disabled={occupancies.loading}/>
                </div>
                <Overlay open={hasOccupancies} className={theme.reset}>
                    <div className={styles.chooseOpenModeHeading}>
                        {t("OpenLockerTitle")}
                    </div>
                    <p>
                        {t("OpenLockerModeQuestion")}
                    </p>
                    <div className={styles.chooseOpenModeButtonContainer}>
                        <PushButton success className={styles.chooseOpenModeButton} onClick={openLocker.execute}>
                            {t("OpenAndKeep")}
                        </PushButton>
                    </div>
                    <div className={styles.chooseOpenModeButtonContainer}>
                        <PushButton danger className={styles.chooseOpenModeButton} onClick={openAndRelease}>
                            {t("OpenAndRelease")}
                        </PushButton>
                    </div>
                </Overlay>
                <MessageOverlay
                    success
                    open={Boolean(openLocker.result)}
                    strings={t}
                    message={t("OpenLockerSuccess")}
                    onClose={onClose}
                    autoClose={10}
                />
                <MessageOverlay
                    error
                    open={Boolean(openErrorMessage)}
                    strings={t}
                    message={openErrorMessage ?? ""}
                    onClose={onClose}
                />
            </div>
        </Dialog>
    );
}

const styles = stylesheet(({
    chooseOpenModeHeading: {
        fontSize: "1.5em",
        fontWeight: 500,
    },
    chooseOpenModeButtonContainer: {
        margin: "1em",
        maxWidth: "18em",
    },
    chooseOpenModeButton: {
        width: "100%"
    },
}));
